import React from 'react';
import { AlertTriangle, Copy, Home, RefreshCw } from 'lucide-react';
import { BUILD_ID } from '../pwa/versionService';
import { recordPwaEvent } from '../pwa/pwaEvents';

// A page's code file could not be downloaded: no internet, or a new version replaced it.
const isChunkError = (error) => /ChunkLoadError|Loading (CSS )?chunk|dynamically imported module|Importing a module script failed/i
  .test(`${error?.name || ''} ${error?.message || ''}`);
const RELOAD_KEY = 'coachhub:chunk-reload';

function describe(error) {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (isChunkError(error)) {
    return offline
      ? { title: 'No internet connection', text: 'This page has not been saved on this phone yet, so it needs the internet to open the first time. Connect to the internet and tap Retry.' }
      : { title: 'CoachHub was updated', text: 'A new version is available. Tap Reload to open the latest version.' };
  }
  if (offline) return { title: 'No internet connection', text: 'Something on this page needs the internet. Connect and tap Retry.' };
  return { title: 'CoachHub could not display this page', text: 'Try again. If the problem continues, share the reference below with support.' };
}

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, reference: null, copied: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('CoachHub ErrorBoundary caught runtime crash:', error, info);
    // After a new release the old page files are gone: reload once to pick up the new version.
    if (isChunkError(error) && navigator.onLine !== false) {
      try {
        if (!sessionStorage.getItem(RELOAD_KEY)) {
          sessionStorage.setItem(RELOAD_KEY, '1');
          window.location.reload();
          return;
        }
      } catch {}
    }
    const reference = `CH-${Date.now().toString(36).toUpperCase()}`;
    this.setState({ reference, error });
    const key = `coachhub:crash:${BUILD_ID}`;
    let count = 1;
    try {
      count = Number(sessionStorage.getItem(key) || 0) + 1;
      sessionStorage.setItem(key, String(count));
    } catch {}
    recordPwaEvent('application-crash', {
      reference,
      buildId: BUILD_ID,
      count,
      name: error?.name,
      message: error?.message,
    });
  }

  reset = () => this.setState({ hasError: false, reference: null, copied: false, error: null });

  repair = async () => {
    try {
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.filter((name) => name.startsWith('coachhub-')).map((name) => caches.delete(name)));
      }
      const registration = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration('/') : null;
      await registration?.update();
    } finally {
      window.location.reload();
    }
  };

  copy = async () => {
    const errorDetails = this.state.error ? `\nError: ${this.state.error.message}\nStack: ${this.state.error.stack}` : '';
    await navigator.clipboard?.writeText(`CoachHub support reference: ${this.state.reference}\nBuild: ${BUILD_ID}${errorDetails}`);
    this.setState({ copied: true });
  };

  componentDidMount() {
    // The app rendered fine, so a later chunk failure may reload again.
    window.setTimeout(() => { try { sessionStorage.removeItem(RELOAD_KEY); } catch {} }, 10000);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    const { title, text } = describe(this.state.error);
    const chunk = isChunkError(this.state.error);
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-950 p-5 text-white">
        <section className="w-full max-w-lg rounded-3xl border border-slate-800 bg-slate-900 p-7 shadow-2xl">
          <AlertTriangle className="h-10 w-10 text-amber-400" />
          <h1 className="mt-4 text-2xl font-bold font-outfit">{title}</h1>
          <p className="mt-2 text-slate-300 text-sm">{text}</p>
          <div className="mt-4 rounded-xl bg-slate-950 p-3 font-mono text-sm border border-slate-800">
            {this.state.reference || 'Creating reference...'}
          </div>

          {this.state.error && (
            <div className="mt-3 p-3 bg-red-950/70 border border-red-800/80 rounded-xl text-left text-xs font-mono text-red-300 max-h-36 overflow-y-auto whitespace-pre-wrap">
              <p className="font-bold text-red-200">{this.state.error.message || String(this.state.error)}</p>
              {this.state.error.stack && (
                <p className="mt-1 text-[10px] text-red-400/80 leading-normal">
                  {this.state.error.stack.split('\n').slice(0, 3).join('\n')}
                </p>
              )}
            </div>
          )}

          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            <button
              onClick={chunk ? () => window.location.reload() : this.reset}
              className="min-h-11 rounded-xl bg-indigo-600 px-4 font-semibold hover:bg-indigo-700 transition-colors"
            >
              <RefreshCw className="mr-2 inline h-4 w-4" />{chunk && navigator.onLine !== false ? 'Reload' : 'Retry'}
            </button>
            <button
              onClick={() => { window.location.href = '/dashboard'; }}
              className="min-h-11 rounded-xl bg-slate-800 px-4 font-semibold hover:bg-slate-700 transition-colors"
            >
              <Home className="mr-2 inline h-4 w-4" />Dashboard
            </button>
            <button
              onClick={this.copy}
              className="min-h-11 rounded-xl bg-slate-800 px-4 font-semibold hover:bg-slate-700 transition-colors"
            >
              <Copy className="mr-2 inline h-4 w-4" />{this.state.copied ? 'Copied' : 'Copy reference'}
            </button>
            <button
              onClick={this.repair}
              className="min-h-11 rounded-xl border border-amber-500/40 px-4 font-semibold text-amber-300 hover:bg-amber-500/10 transition-colors"
            >
              Repair app cache
            </button>
          </div>
        </section>
      </main>
    );
  }
}

export default ErrorBoundary;
